/**
 * 살림 가구·소품 — 교자상·방석·문갑·서안·변기·세면대·서류 더미·나무 궤짝·녹음기.
 *
 * 단서가 나오는 가구는 서랍·문이 「열릴 수 있게」 보여야 한다.
 */

import * as THREE from 'three';
import { bevelBox, cappedArc, frameMolding, lathe, moldingProfile, shellBox } from './kernel';
import { paintGeometry, type Piece } from './pieces';

const OBANG = ['#2f5fa8', '#e8e4dc', '#a82b24', '#1f1c1a', '#d8ac33'] as const;

/** 시드 난수 — 같은 방을 모두가 똑같이 봐야 한다 */
function rnd(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const HOUSEHOLD_PIECES: Piece[] = [
  // ============ 대기방 ============
  {
    id: 'gyoja_sang',
    name: '교자상',
    collection: 'sahol',
    category: '대기방',
    note: '다섯이 둘러앉는 좌식 큰상. 접는 다리에 경첩이 보이고, 상판 가장자리에 손때가 탄 몰딩이 둘러 있다. 도입부 대화 장면의 무대.',
    dims: { w: 1.35, d: 0.82, h: 0.32, topT: 0.028, legT: 0.045 },
    openable: [
      { part: '다리 왼쪽', swing: 1.4, axis: 'z', pivot: [-0.555, 0.289, 0] },
      { part: '다리 오른쪽', swing: -1.4, axis: 'z', pivot: [0.555, 0.289, 0] },
    ],
    build(w, d) {
      w.part('상판');
      const top = bevelBox(d.w, d.topT, d.d, 0.005, 2);
      top.translate(0, d.h - d.topT / 2, 0);
      w.add(top, 'lacquer', 0.8);
      const prof = moldingProfile(0.016, d.topT * 0.85, [
        { kind: 'bead', at: 0.32, spread: 0.18, depth: 0.5 },
      ]);
      const m = frameMolding(d.w, d.d, prof);
      m.translate(0, d.h - d.topT, 0);
      w.add(m, 'lacquer', 0.4);

      w.part('접는 다리');
      // 교자상 다리는 상판 안쪽으로 물려 접힌다 — 경첩이 보여야 '접는 상'으로 읽힌다
      const inset = 0.12;
      for (const sx of [-1, 1]) {
        const legY = (d.h - d.topT) / 2;
        /* 다리+버팀은 경첩 축(z)으로 상판 아래 접힌다 — 경첩 조각 자체는 상판에 붙은 채 (2026-09-16) */
        w.movable(sx < 0 ? '다리 왼쪽' : '다리 오른쪽', () => {
          const frame = bevelBox(d.legT, d.h - d.topT, d.d - inset * 2, 0.004);
          frame.translate((sx * (d.w - inset * 2)) / 2, legY, 0);
          w.add(frame, 'lacquer', 0.5);
          // 가로 버팀
          const brace = bevelBox(d.legT * 0.7, d.legT * 0.7, d.d - inset * 2 - 0.04, 0.003);
          brace.translate((sx * (d.w - inset * 2)) / 2, d.h - d.topT - 0.05, 0);
          w.add(brace, 'lacquer', 0.4);
        });
        // 경첩
        for (const sz of [-1, 1]) {
          const hinge = bevelBox(0.03, 0.006, 0.05, 0.002);
          hinge.translate(
            (sx * (d.w - inset * 2)) / 2,
            d.h - d.topT - 0.003,
            (sz * (d.d - inset * 2)) / 2 - sz * 0.03,
          );
          w.add(hinge, 'iron', 0.2);
        }
      }
    },
  },

  {
    id: 'bangseok',
    name: '방석',
    collection: 'sahol',
    category: '대기방',
    note: '오방색 테두리를 두른 전통 방석. 눌린 자국이 남아 있어 누군가 오래 앉아 있었음을 말한다.',
    dims: { w: 0.44, d: 0.44, h: 0.06 },
    openable: [{ part: '방석', slide: [0, 0.05, 0] }],
    build(w, d) {
      /* 방석을 들어 아래를 본다 — 아래 숨겨둔 게 보이는 자리 (2026-09-16) */
      w.movable('방석', () => {
      w.part('속');
      // 가운데가 눌린 방석 — 완전한 직육면체면 스펀지 블록으로 보인다
      const seg = 5;
      for (let ix = 0; ix < seg; ix++) {
        for (let iz = 0; iz < seg; iz++) {
          const u = (ix + 0.5) / seg - 0.5;
          const v = (iz + 0.5) / seg - 0.5;
          const dip = 1 - Math.exp(-((u * u + v * v) / 0.06)) * 0.42;
          const cell = bevelBox(d.w / seg + 0.002, d.h * dip, d.d / seg + 0.002, 0.006, 2);
          cell.translate(u * d.w, (d.h * dip) / 2, v * d.d);
          w.add(cell, 'fabric', 0.35);
        }
      }
      w.part('오방색 테두리');
      // 네 변에 색을 나눠 두른다 (오방색은 순서가 정해져 있다)
      const edges: [number, number, number, number][] = [
        [0, -d.d / 2, d.w, 0],
        [d.w / 2, 0, d.d, Math.PI / 2],
        [0, d.d / 2, d.w, 0],
        [-d.w / 2, 0, d.d, Math.PI / 2],
      ];
      edges.forEach(([x, z, len, rot], i) => {
        const g = bevelBox(len + 0.02, d.h * 0.72, 0.022, 0.004);
        g.rotateY(rot);
        g.translate(x, d.h * 0.4, z);
        paintGeometry(g, OBANG[i]);
        w.add(g, 'paint', 0.3);
      });
      w.part('중앙 매듭');
      const knot = lathe(
        [
          [0.014, 0],
          [0.02, 0.006],
          [0.012, 0.014],
        ],
        12,
      );
      knot.translate(0, d.h * 0.62, 0);
      paintGeometry(knot, OBANG[4]);
      w.add(knot, 'paint', 0.1);
      });
    },
  },

  {
    id: 'mungap',
    name: '문갑',
    collection: 'sahol',
    category: '대기방',
    note: '벽에 붙여 두는 낮고 긴 수납장. 서랍 하나가 반쯤 열려 있다 — 접견실 서랍의 협박 편지가 나오는 자리.',
    /** 서랍 하나가 13cm 빠져 있는 것이 의도다 — 「누가 뒤졌다」는 신호 (아래 out 값) */
    clusters: 2,
    dims: { w: 1.16, d: 0.32, h: 0.42, drawers: 4, legH: 0.05 },
    openable: [
      { part: '서랍1', slide: [0, 0, -0.2], inside: { w: 0.24, h: 0.2, d: 0.26, at: [-0.42, 0.14, 0] } },
      { part: '서랍2', slide: [0, 0, -0.14], inside: { w: 0.24, h: 0.2, d: 0.26, at: [-0.14, 0.14, -0.13] } },
      { part: '서랍3', slide: [0, 0, -0.2], inside: { w: 0.24, h: 0.2, d: 0.26, at: [0.14, 0.14, 0] } },
      { part: '서랍4', slide: [0, 0, -0.2], inside: { w: 0.24, h: 0.2, d: 0.26, at: [0.42, 0.14, 0] } },
    ],
    build(w, d) {
      const bodyH = d.h - d.legH;
      w.part('몸통');
      const carcass = bevelBox(d.w, bodyH, d.d, 0.005, 2);
      carcass.translate(0, d.legH + bodyH / 2, 0);
      w.add(carcass, 'walnut', 0.7);
      const topPlate = bevelBox(d.w + 0.03, 0.016, d.d + 0.025, 0.004);
      topPlate.translate(0, d.h - 0.008, 0);
      w.add(topPlate, 'walnut', 0.6);

      w.part('서랍');
      const dw = (d.w - 0.05) / d.drawers;
      for (let i = 0; i < d.drawers; i++) {
        const x = -d.w / 2 + 0.025 + dw * (i + 0.5);
        // 두 번째 서랍만 반쯤 빠져 있다 — 누가 뒤졌다는 신호. 넷 다 움직이는 부품(2026-09-15): 빠진 것도 거기서 더 빠진다
        const out = i === 1 ? 0.13 : 0;
        w.movable(`서랍${i + 1}`, () => {
          const face = bevelBox(dw - 0.012, bodyH - 0.05, 0.02, 0.004);
          face.translate(x, d.legH + bodyH / 2, -d.d / 2 - 0.01 - out);
          w.add(face, 'walnut', 0.55);
          // 서랍 상자 — 앞판 뒤로 몸통 깊이만큼. 열면 속이 보인다
          const bd = d.d - 0.04, bh = bodyH - 0.09;
          for (const g of shellBox(dw - 0.03, bh, bd, 0.009, ['+y'])) { g.translate(x, d.legH + bodyH / 2 - bh / 2, -d.d / 2 + bd / 2 - out + 0.005); w.add(g, 'walnut', 0.5); }
          const fy = d.legH + bodyH / 2 - bh / 2 + 0.012, fz = -d.d / 2 + bd / 2 - out + 0.005;
          if (i === 1) { const s1 = bevelBox(dw - 0.09, 0.01, bd - 0.08, 0.002); s1.translate(x, fy + 0.005, fz); w.add(s1, 'paper', 0.5); }   // 편지 봉투가 나오는 서랍
          else if (i === 0) { const c = bevelBox(0.06, 0.04, 0.06, 0.004); c.translate(x - 0.02, fy + 0.02, fz + 0.03); w.add(c, 'lacquer', 0.4); }
          else if (i === 3) { const r = bevelBox(dw - 0.1, 0.03, bd - 0.1, 0.008, 2); r.translate(x, fy + 0.015, fz); w.add(r, 'fabric', 0.5); }
          // 무쇠 들손
          const pull = new THREE.TorusGeometry(0.019, 0.0035, 6, 16);
          pull.rotateX(Math.PI / 2);
          pull.translate(x, d.legH + bodyH / 2 - 0.012, -d.d / 2 - 0.018 - out);
          w.add(pull, 'iron', 0.15);
          const plate = bevelBox(0.05, 0.03, 0.003, 0.001);
          plate.translate(x, d.legH + bodyH / 2, -d.d / 2 - 0.011 - out);
          w.add(plate, 'iron', 0.2);
        });
      }

      w.part('굽');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const foot = bevelBox(0.06, d.legH, 0.05, 0.004);
          foot.translate((sx * (d.w - 0.09)) / 2, d.legH / 2, (sz * (d.d - 0.07)) / 2);
          w.add(foot, 'walnut', 0.4);
        }
      }
    },
  },

  {
    id: 'seoan',
    name: '서안과 신장경',
    collection: 'sahol',
    category: '퍼즐',
    note: '다락에 놓인 낮은 경상. 펼쳐진 경전에 협동 퍼즐 힌트가 적혀 있다 — 위층에서 읽어주고 아래층에서 맞추는 그 책.',
    dims: { w: 0.62, d: 0.34, h: 0.26, topT: 0.022 },
    openable: [{ part: '윗장', swing: 2.9, axis: 'z', pivot: [0, 0.275, 0] }],
    build(w, d) {
      w.part('경상');
      const top = bevelBox(d.w, d.topT, d.d, 0.004, 2);
      top.translate(0, d.h - d.topT / 2, 0);
      w.add(top, 'lacquer', 0.7);
      // 경상은 양 끝이 위로 말려 올라간다 (책이 미끄러지지 않게)
      for (const s of [-1, 1]) {
        const curl = lathe(
          [
            [0.001, 0],
            [0.026, 0],
            [0.026, 0.028],
            [0.001, 0.028],
          ],
          10,
        );
        curl.rotateZ(Math.PI / 2);
        curl.rotateY(Math.PI / 2);
        curl.translate((s * d.w) / 2, d.h - d.topT / 2, 0);
        curl.scale(1, 1, (d.d / 0.052) * 0.9);
        w.add(curl, 'lacquer', 0.4);
      }
      w.part('다리');
      for (const s of [-1, 1]) {
        const leg = bevelBox(0.03, d.h - d.topT, d.d - 0.08, 0.004);
        leg.translate((s * (d.w - 0.12)) / 2, (d.h - d.topT) / 2, 0);
        w.add(leg, 'lacquer', 0.4);
        const foot = bevelBox(0.05, 0.016, d.d - 0.05, 0.003);
        foot.translate((s * (d.w - 0.12)) / 2, 0.008, 0);
        w.add(foot, 'lacquer', 0.3);
      }

      w.part('신장경');
      // 펼친 책 — 좌우 면이 가운데서 살짝 솟는다
      const pages = 7;
      for (let i = 0; i < pages; i++) {
        const t = i / pages;
        const lift = Math.sin((1 - t) * 1.2) * 0.006;
        for (const s of [-1, 1]) {
          const drawLeaf = () => {
            const leaf = bevelBox(d.w * 0.3, 0.0018, d.d * 0.62, 0.0006);
            leaf.rotateZ(s * (0.04 + t * 0.02));
            leaf.translate(s * d.w * 0.16, d.h + 0.004 + i * 0.0016 + lift, 0);
            w.add(leaf, 'paper', 0.5);
          };
          /* 맨 위 오른쪽 장은 넘겨본다 — π 가 아니라 2.9 라 왼쪽 장 더미 살짝 위에 뜬다 (2026-09-16) */
          if (i === pages - 1 && s === 1) w.movable('윗장', drawLeaf);
          else drawLeaf();
        }
      }
      const spine = bevelBox(0.016, 0.008, d.d * 0.62, 0.002);
      spine.translate(0, d.h + 0.012, 0);
      w.add(spine, 'redcloth', 0.2);
    },
  },

  {
    id: 'byeongi',
    glb: '/models/sahol/byeongi',
    /* 뚜껑·시트는 GLB 의 'lid'·'seat' 노드. 경첩은 시트 뒤끝(탱크 앞), 뒤로 젖혀 탱크에 기댄다.
       물탱크 뚜껑은 들어 올린다 — 탱크 안을 들여다보는 자리 (2026-09-16) */
    openable: [
      { part: '뚜껑', node: 'lid', swing: 1.75, axis: 'x', pivot: [0, 0.43, 0.18] },   // Rx(+) 가 앞(−Z)을 들어 올린다. 경첩은 시트 뒤끝   // 블렌더 GLB (tools/blender/bath.py, 2026-09-15 Chris: 「변기랑 세면대 좀 아쉬운데」). 아래 build 는 대체이자 크기 계약
      { part: '시트', node: 'seat', swing: 1.75, axis: 'x', pivot: [0, 0.43, 0.18] },
      { part: '물탱크 뚜껑', node: 'tank_lid', slide: [0, 0.14, 0], inside: { w: 0.3, h: 0.3, d: 0.14, at: [0, 0.62, 0.27] } },
    ],
    name: '변기',
    collection: 'sahol',
    category: '화장실',
    /* 사건 메모: 한지영이 「여기 있었다」고 말한 방이다. 아무 흔적이 없다는 것 자체가 단서라, 이 방은 깨끗해야 한다. */
    note: '오래 쓴 양변기. 바닥에서 허리가 잘록하게 들어갔다 다시 벌어지는 몸통 위에 시트와 물탱크가 얹힌다. 뚜껑은 시트 뒤끝을 경첩으로 뒤로 젖혀 탱크에 기댄다(openable). dims.tankH 로 탱크 높이를 정한다.',
    dims: { w: 0.37, d: 0.66, h: 0.4, tankH: 0.38 },
    build(w, d) {
      w.part('몸통');
      // 변기는 바닥에서 허리가 잘록하게 들어갔다 다시 벌어진다. 통짜 상자로는 안 읽힌다
      const body = lathe(
        [
          [d.w * 0.42, 0],
          [d.w * 0.4, 0.03],
          [d.w * 0.26, d.h * 0.34],
          [d.w * 0.34, d.h * 0.62],
          [d.w * 0.47, d.h * 0.9],
          [d.w * 0.5, d.h],
        ],
        20,
      );
      body.scale(1, 1, 1.24);
      // **볼은 -Z(정면), 물탱크는 +Z(뒷면)** 이다. 반대로 짜여 있어서 앞에서 보면
      // 물탱크의 넓은 면이 화면을 채우고 변기 볼이 그 뒤에 가려 안 보였다 — 금고에서
      // 겪은 것과 같은 앞뒤 뒤집힘이다. 조각 규약은 정면이 -Z 다.
      body.translate(0, 0, -0.03);
      w.add(body, 'porcelain', 0.5);
      // 앉는 자리 — 위에서 보면 타원이다
      const rim = new THREE.TorusGeometry(d.w * 0.46, 0.026, 8, 22);
      rim.rotateX(Math.PI / 2);
      rim.scale(1, 1, 1.28);
      rim.translate(0, d.h + 0.012, -0.03);
      w.add(rim, 'porcelain', 0.2);

      w.part('시트');
      const seat = new THREE.TorusGeometry(d.w * 0.44, 0.017, 8, 22);
      seat.rotateX(Math.PI / 2);
      seat.scale(1, 1, 1.28);
      seat.translate(0, d.h + 0.042, -0.03);
      w.add(seat, 'paint', 0.2);
      // 뚜껑은 열려 벽에 기대 있다
      const lid = bevelBox(d.w * 0.92, 0.022, d.d * 0.72, 0.008, 2);
      lid.rotateX(1.32);
      lid.translate(0, d.h + 0.34, d.d * 0.34);
      w.add(lid, 'paint', 0.4);

      w.part('물탱크');
      const tank = bevelBox(d.w * 1.06, d.tankH, 0.19, 0.012, 2);
      tank.translate(0, d.h + d.tankH / 2 - 0.02, d.d / 2 - 0.06);
      w.add(tank, 'porcelain', 0.5);
      const tankLid = bevelBox(d.w * 1.12, 0.026, 0.22, 0.006);
      tankLid.translate(0, d.h + d.tankH, d.d / 2 - 0.06);
      w.add(tankLid, 'porcelain', 0.3);
      const lever = new THREE.CylinderGeometry(0.012, 0.012, 0.045, 10);
      lever.rotateZ(Math.PI / 2);
      lever.translate(d.w * 0.4, d.h + d.tankH * 0.72, d.d / 2 - 0.14);
      w.add(lever, 'steel', 0.05);
    },
  },

  {
    id: 'semyeondae',
    glb: '/models/sahol/semyeondae',
    /* 거울장 문 — GLB 의 'mirror_door' 노드. 경첩은 문 왼쪽 모서리(x −0.245), 안에 약병·연고·칫솔컵 (2026-09-16).
       연고·알약통 뚜껑도 따로 딴다 — 거울장 안의 물건도 뒤져야 한다 */
    openable: [
      { part: '거울장 문', node: 'mirror_door', swing: -2.0, axis: 'y', pivot: [-0.245, 1.33, -0.086] },   // 블렌더 GLB (tools/blender/bath.py). 거울장까지 한 덩이
      { part: '연고 뚜껑', node: 'ointment_cap', slide: [0, 0.06, 0] },
      { part: '알약통 뚜껑', node: 'pill_cap', slide: [0, 0.06, 0] },
    ],
    name: '세면대',
    collection: 'sahol',
    category: '화장실',
    note: '기둥 세면대와 거울장. 수도꼭지 아래 물때가 앉았고 배수관이 그대로 드러나 있다.',
    /** 거울장은 **벽에 붙는 별개 물건**이다 — 세면대 기둥과 떨어져 있는 것이 맞다 */
    clusters: 2,
    dims: { w: 0.52, d: 0.42, h: 0.82 },
    build(w, d) {
      w.part('기둥');
      const col = lathe(
        [
          [d.w * 0.26, 0],
          [d.w * 0.22, 0.04],
          [d.w * 0.16, d.h * 0.5],
          [d.w * 0.2, d.h * 0.86],
        ],
        16,
      );
      w.add(col, 'porcelain', 0.4);

      w.part('대야');
      // 위가 넓고 안이 파인 그릇. 옆에서 봤을 때 두께가 보여야 도기로 읽힌다
      const basin = lathe(
        [
          [d.w * 0.2, 0],
          [d.w * 0.44, 0.06],
          [d.w * 0.5, 0.14],
          [d.w * 0.5, 0.17],
          [d.w * 0.44, 0.15],
          [d.w * 0.36, 0.05],
          [d.w * 0.2, 0.02],
          [d.w * 0.2, 0],
        ],
        20,
      );
      basin.scale(1, 1, 0.82);
      basin.translate(0, d.h * 0.86, 0);
      w.add(basin, 'porcelain', 0.4);

      w.part('수도꼭지');
      const spout = cappedArc(0.055, 0.011, Math.PI * 0.62, 14, 7);
      spout.rotateY(Math.PI / 2);
      spout.rotateZ(0.3);
      spout.translate(0, d.h * 0.86 + 0.13, d.w * 0.28);   // 벽 쪽(+Z). 예전엔 −Z 라 벽에 대면 꼭지·거울장이 방 쪽으로 튀어나왔다(2026-09-15 Chris 컷)
      w.add(spout, 'steel', 0.1);
      for (const sx of [-1, 1]) {
        const knob = lathe(
          [
            [0.016, 0],
            [0.02, 0.012],
            [0.012, 0.024],
          ],
          10,
        );
        knob.translate(sx * 0.07, d.h * 0.86 + 0.11, d.w * 0.28);
        w.add(knob, 'steel', 0.05);
      }

      w.part('배수관');
      const drain = new THREE.CylinderGeometry(0.019, 0.019, 0.16, 10);
      drain.translate(0, d.h * 0.78, 0);
      w.add(drain, 'steel', 0.1);
      const trap = cappedArc(0.045, 0.019, Math.PI, 14, 7);
      trap.rotateY(Math.PI / 2);
      trap.translate(0, d.h * 0.7, 0.045);
      w.add(trap, 'steel', 0.1);

      w.part('거울장');
      const cab = bevelBox(d.w * 0.96, 0.5, 0.12, 0.008, 2);
      cab.translate(0, d.h + 0.44, d.w * 0.3);
      w.add(cab, 'paint', 0.4);
      const mirror = bevelBox(d.w * 0.84, 0.42, 0.01, 0.003);
      mirror.translate(0, d.h + 0.44, d.w * 0.3 - 0.065);
      w.add(mirror, 'glass', 0.3);
    },
  },

  {
    id: 'seoryu_deomi',
    glb: '/models/sahol/paper_pile',   // 블렌더 GLB (tools/blender/props.py). 아래 build 는 대체이자 크기 계약
    nofront: true,
    name: '서류 더미',
    collection: 'sahol',
    category: '사무실',
    note: '장부와 서류철이 쌓인 더미. 반듯하게 쌓이지 않고 귀가 어긋나야 사람이 뒤진 자리로 읽힌다.',
    dims: { w: 0.24, d: 0.33, count: 14 },
    openable: [{ part: '서류철', slide: [0, 0, -0.15] }],
    build(w, d) {
      const r2 = rnd(2024);
      w.part('종이');
      let y = 0;
      for (let i = 0; i < d.count; i++) {
        const t = 0.004 + r2() * 0.006;
        const g = bevelBox(d.w, t, d.d, 0.0008);
        g.rotateY((r2() - 0.5) * 0.22);
        g.translate((r2() - 0.5) * 0.022, y + t / 2, (r2() - 0.5) * 0.022);
        w.add(g, 'paper', 0.4);
        y += t;
      }
      w.part('서류철');
      // 맨 위 한 권만 표지가 다르다 — 여기부터 보라는 신호.
      // 더미에서 빼내 보는 여닫이다 (2026-09-16)
      w.movable('서류철', () => {
        const cover = bevelBox(d.w * 1.04, 0.018, d.d * 1.02, 0.003);
        cover.rotateY(0.16);
        cover.translate(0.01, y + 0.009, -0.008);
        w.add(cover, 'ebony', 0.3);
        const tag = bevelBox(d.w * 0.3, 0.002, 0.05, 0.001);
        tag.rotateY(0.16);
        tag.translate(0.01, y + 0.019, d.d * 0.4);
        w.add(tag, 'redcloth', 0.05);
      });
    },
  },

  {
    id: 'namu_sangja',
    nofront: true,
    name: '나무 궤짝',
    collection: 'sahol',
    category: '지하',
    note: '지하에 쌓아 둔 짐 궤짝. 널판 사이가 벌어져 안이 비친다.',
    dims: { w: 0.56, d: 0.4, h: 0.36, slats: 5 },
    /** 뚜껑이 뒤 모서리에서 젖혀진다 — 널판 궤짝 (2026-09-15) */
    openable: [{ part: '뚜껑', swing: -2.0, axis: 'x', pivot: [0, 0.37, 0.2], inside: { w: 0.44, h: 0.22, d: 0.3, at: [0, 0.16, 0] } }],
    build(w, d) {
      w.part('널판');
      // 궤짝은 널을 이어 붙인 것이다. 판마다 틈이 있어야 상자가 아니라 궤짝으로 보인다
      const gap = 0.008;
      const sh = (d.h - gap * (d.slats - 1)) / d.slats;
      for (let i = 0; i < d.slats; i++) {
        const y = sh / 2 + i * (sh + gap);
        for (const [sz, len] of [
          [-1, d.w],
          [1, d.w],
        ] as [number, number][]) {
          const g = bevelBox(len, sh, 0.016, 0.003);
          g.translate(0, y, (sz * d.d) / 2);
          w.add(g, 'oak', 0.35);
        }
        for (const sx of [-1, 1]) {
          const g = bevelBox(0.016, sh, d.d - 0.032, 0.003);
          g.translate((sx * d.w) / 2, y, 0);
          w.add(g, 'oak', 0.35);
        }
      }
      w.part('모서리 기둥');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const g = bevelBox(0.03, d.h, 0.03, 0.004);
          g.translate((sx * (d.w - 0.03)) / 2, d.h / 2, (sz * (d.d - 0.03)) / 2);
          w.add(g, 'oak', 0.2);
        }
      }
      w.part('뚜껑');
      w.movable('뚜껑', () => {
        const lid = bevelBox(d.w + 0.03, 0.02, d.d + 0.03, 0.005, 2);
        lid.translate(0, d.h + 0.01, 0);
        w.add(lid, 'oak', 0.5);
        const grip = bevelBox(0.12, 0.014, 0.03, 0.004);   // 들손 — 뚜껑과 같이 움직인다
        grip.translate(0, d.h + 0.027, -d.d / 2 + 0.06);
        w.add(grip, 'iron', 0.15);
      });
      w.part('속');   // 열면 보이는 것 — 접은 천과 신문 뭉치
      const fold = bevelBox(d.w - 0.1, 0.05, d.d - 0.1, 0.012, 2); fold.translate(0, 0.04, 0); w.add(fold, 'fabric', 0.6);
      const paper = bevelBox(d.w - 0.18, 0.03, d.d - 0.2, 0.004); paper.rotateY(0.12); paper.translate(0.02, 0.08, 0.02); w.add(paper, 'paper', 0.5);
      w.part('쇠테');
      for (const sx of [-1, 1]) {
        const band = bevelBox(0.026, d.h + 0.03, 0.004, 0.001);
        band.translate((sx * d.w) / 2.6, (d.h + 0.02) / 2, d.d / 2 + 0.009);
        w.add(band, 'iron', 0.1);
      }
    },
  },

  {
    id: 'nokeumgi',
    glb: '/models/sahol/nokeumgi',
    name: '녹음기와 일기',
    collection: 'sahol',
    category: '도구',
    /* 사건 메모: 강만신이 남긴 카세트 녹음기와 일기장. 태주를 어떻게 가둬 길렀는지가 여기서 나온다 — 반전의 결정적 증거. */
    note: '휴대용 카세트 녹음기와 그 옆에 놓인 일기장, 두 물건이 한 조각이다. 녹음기는 윗면의 카세트 창과 릴 두 개로 녹음기로 읽힌다. dims 는 녹음기 본체 크기다.',
    /** 이름 그대로 **녹음기와 일기장 두 물건**이다 — 나란히 놓인 것이 의도다 */
    clusters: 2,
    dims: { w: 0.15, d: 0.09, h: 0.042 },
    openable: [{ part: '카세트 테이프', slide: [0, 0.05, 0] }],
    build(w, d) {
      w.part('녹음기');
      const body = bevelBox(d.w, d.h, d.d, 0.005, 2);
      body.translate(0, d.h / 2, 0);
      w.add(body, 'ebony', 0.3);
      // 카세트 창
      const win = bevelBox(d.w * 0.44, d.h * 0.5, 0.002, 0.001);
      win.rotateX(Math.PI / 2);
      win.translate(-d.w * 0.16, d.h + 0.001, 0);
      w.add(win, 'glass', 0.1);
      // 릴 두 개
      for (const s of [-1, 1]) {
        const reel = lathe(
          [
            [0.004, 0],
            [0.013, 0.002],
            [0.013, 0.005],
            [0.004, 0.006],
          ],
          14,
        );
        reel.translate(-d.w * 0.16 + s * 0.026, d.h + 0.0015, 0);
        w.add(reel, 'paper', 0.06);
      }
      // 버튼 다섯 개
      for (let i = 0; i < 5; i++) {
        const btn = bevelBox(0.017, 0.006, 0.014, 0.002);
        btn.translate(d.w * 0.1 + (i - 2) * 0.019 * 0.55, d.h + 0.002, d.d * 0.26);
        // 재생 버튼만 붉게 — 나머지는 몸통과 같은 금속 슬롯으로 묶어 draw call 을 아낀다
        w.add(btn, i === 2 ? 'blood' : 'iron', 0.06);
      }
      // 스피커 구멍
      for (let i = 0; i < 5; i++) {
        for (let k = 0; k < 3; k++) {
          const hole = lathe(
            [
              [0, 0],
              [0.0024, 0],
              [0.0024, 0.0015],
              [0, 0.0015],
            ],
            8,
          );
          hole.translate(d.w * 0.24 + (i - 2) * 0.007, d.h + 0.0008, -d.d * 0.2 + k * 0.007);
          w.add(hole, 'iron', 0.04);
        }
      }

      w.part('일기장');
      const bookW = 0.13;
      const bookD = 0.085;
      const cover = bevelBox(bookW, 0.006, bookD, 0.002);
      cover.rotateY(0.28);
      cover.translate(0.02, 0.003, -0.13);
      w.add(cover, 'ebony', 0.3);
      for (let i = 0; i < 9; i++) {
        const leaf = bevelBox(bookW - 0.008, 0.0015, bookD - 0.006, 0.0006);
        leaf.rotateY(0.28 + (i % 3) * 0.006);
        leaf.translate(0.02, 0.007 + i * 0.0016, -0.13);
        w.add(leaf, 'paper', 0.4);
      }
      const topCover = bevelBox(bookW, 0.006, bookD, 0.002);
      topCover.rotateY(0.28);
      topCover.translate(0.02, 0.007 + 9 * 0.0016 + 0.003, -0.13);
      w.add(topCover, 'ebony', 0.3);
      // 끼워둔 붉은 끈
      const mark = bevelBox(0.008, 0.001, bookD * 0.9, 0.0004);
      mark.rotateY(0.28);
      mark.translate(0.045, 0.007 + 5 * 0.0016, -0.145);
      w.add(mark, 'blood', 0.1);

      /* **카세트 테이프** — 녹음기 옆에 놓인 테이프 한 개. glb 조각이라 곁가지로
       * 얹어 집어 드는 여닫이로 쓴다 (2026-09-16) */
      w.movable('카세트 테이프', () => {
        const tape = bevelBox(0.1, 0.012, 0.065, 0.002);
        tape.rotateY(-0.22);
        tape.translate(0.1, 0.006, 0.02);
        w.add(tape, 'ebony', 0.15);
        const label = bevelBox(0.07, 0.001, 0.04, 0.0005);
        label.rotateY(-0.22);
        label.translate(0.1, 0.0125, 0.02);
        w.add(label, 'paper', 0.1);
        // 릴 구멍 두 개
        for (const s of [-1, 1]) {
          const hole = new THREE.CylinderGeometry(0.006, 0.006, 0.002, 10);
          hole.translate(0.1 + s * 0.02, 0.013, 0.02);
          w.add(hole, 'iron', 0.05);
        }
      });
    },
  },
];
